import React, { useEffect, useState } from 'react';
import { Layout, Button, Avatar, Dropdown, Space, Drawer } from 'antd';
import { 
  UserOutlined, LogoutOutlined, DashboardOutlined, TeamOutlined, 
  KeyOutlined, MenuOutlined, FileOutlined, SettingOutlined, MenuUnfoldOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { authAPI, menuAPI } from '../services/api';

const { Header } = Layout;

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem('access_token');
  const [me, setMe] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [menus, setMenus] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);

  // 图标映射
  const iconMap = {
    'DashboardOutlined': <DashboardOutlined />,
    'TeamOutlined': <TeamOutlined />,
    'KeyOutlined': <KeyOutlined />,
    'MenuOutlined': <MenuOutlined />,
    'FileOutlined': <FileOutlined />,
    'SettingOutlined': <SettingOutlined />,
    'MenuUnfoldOutlined': <MenuUnfoldOutlined />,
  };

  const getIcon = (iconName) => {
    return iconMap[iconName] || <FileOutlined />;
  };

  const renderMenu = (menuList) => {
    return menuList.map(menu => {
      if (menu.children && menu.children.length > 0) {
        // 父菜单，暂时不支持下拉菜单，显示子菜单
        return menu.children.map(child => (
          <Button
            key={child.id}
            type={isSelected(child.path, child) ? 'primary' : 'text'}
            icon={getIcon(child.icon)}
            onClick={() => handleMenuClick(child)}
            style={{
              color: isSelected(child.path, child) ? '#fff' : '#e6edf3',
              background: isSelected(child.path, child) ? '#238636' : 'transparent',
              border: isSelected(child.path, child) ? '1px solid #238636' : '1px solid #30363d',
              '&:hover': {
                background: isSelected(child.path, child) ? '#2ea043' : '#21262d',
                color: '#e6edf3'
              }
            }}
          >
            {child.name}
          </Button>
        ));
      } else {
        // 子菜单或无子菜单
        return (
          <Button
            key={menu.id}
            type={isSelected(menu.path, menu) ? 'primary' : 'text'}
            icon={getIcon(menu.icon)}
            onClick={() => handleMenuClick(menu)}
            style={{
              color: isSelected(menu.path, menu) ? '#fff' : '#e6edf3',
              background: isSelected(menu.path, menu) ? '#238636' : 'transparent',
              border: isSelected(menu.path, menu) ? '1px solid #238636' : '1px solid #30363d',
              '&:hover': {
                background: isSelected(menu.path, menu) ? '#2ea043' : '#21262d',
                color: '#e6edf3'
              }
            }}
          >
            {menu.name}
          </Button>
        );
      }
    });
  };

  const handleMenuClick = (menu) => {
    if (menu.menu_type === 'FILE') {
      // 文件类型菜单，跳转到文件查看页面
      navigate(`/file/${menu.id}`);
    } else {
      // 路由类型菜单
      if (menu.path) {
        navigate(menu.path);
      }
    }
  };

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      try {
        const resp = await authAPI.getMe();
        if (mounted) {
          setMe(resp.data);
          setIsAdmin(resp.data.is_admin);
        }
      } catch (e) {
        if (mounted) {
          setMe(null);
          setIsAdmin(false);
        }
      }
    };
    const fetchMenus = async () => {
      try {
        const resp = await menuAPI.listMenus();
        if (mounted) {
          setMenus(resp.data || resp);
        }
      } catch (e) {
        if (mounted) {
          setMenus([]);
        }
      }
    };
    if (token) {
      run();
      fetchMenus();
    }
    return () => {
      mounted = false;
    };
  }, [token]);

  const handleLogout = () => {
    import('antd').then(({ Modal }) => {
      Modal.confirm({
        title: '确认退出登录',
        content: '确定要退出当前账号吗？',
        okText: '确认退出',
        cancelText: '取消',
        okButtonProps: { danger: true },
        styles: {
          body: {
            color: '#ffffff !important'
          },
          title: {
            color: '#ffffff !important'
          },
          content: {
            color: '#ffffff !important'
          }
        },
        onOk: async () => {
          try {
            await authAPI.logout();
          } catch (error) {
            console.error('退出登录失败:', error);
          } finally {
            localStorage.removeItem('access_token');
            localStorage.removeItem('refresh_token');
            localStorage.removeItem('username');
            localStorage.removeItem('activation_code');
            navigate('/login');
          }
        },
      });
    });
  };

  const getSelectedPath = () => {
    return location.pathname;
  };

  const isSelected = (path, menu) => {
    // 如果是文件类型菜单，检查当前路径是否匹配文件路由
    if (menu && menu.menu_type === 'FILE') {
      return location.pathname === `/file/${menu.id}`;
    }
    // 路由类型菜单按原逻辑检查
    return location.pathname === path;
  };

  const userMenuItems = [
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      onClick: handleLogout,
    },
  ];

  if (!token) {
    return null;
  }

  const renderMobileMenu = () => {
    return (
      <div style={{ padding: '16px' }}>
        <div style={{ marginBottom: '16px', fontSize: '18px', fontWeight: 'bold', color: '#58a6ff' }}>
          菜单
        </div>
        {menus.map(menu => {
          if (menu.children && menu.children.length > 0) {
            return menu.children.map(child => (
              <div key={child.id} style={{ marginBottom: '8px' }}>
                <Button
                  block
                  type={isSelected(child.path, child) ? 'primary' : 'text'}
                  icon={getIcon(child.icon)}
                  onClick={() => {
                    handleMenuClick(child);
                    setDrawerVisible(false);
                  }}
                  style={{
                    color: isSelected(child.path, child) ? '#fff' : '#e6edf3',
                    background: isSelected(child.path, child) ? '#238636' : 'transparent',
                    border: isSelected(child.path, child) ? '1px solid #238636' : '1px solid #30363d',
                    textAlign: 'left',
                    height: '48px'
                  }}
                >
                  {child.name}
                </Button>
              </div>
            ));
          } else {
            return (
              <div key={menu.id} style={{ marginBottom: '8px' }}>
                <Button
                  block
                  type={isSelected(menu.path, menu) ? 'primary' : 'text'}
                  icon={getIcon(menu.icon)}
                  onClick={() => {
                    handleMenuClick(menu);
                    setDrawerVisible(false);
                  }}
                  style={{
                    color: isSelected(menu.path, menu) ? '#fff' : '#e6edf3',
                    background: isSelected(menu.path, menu) ? '#238636' : 'transparent',
                    border: isSelected(menu.path, menu) ? '1px solid #238636' : '1px solid #30363d',
                    textAlign: 'left',
                    height: '48px'
                  }}
                >
                  {menu.name}
                </Button>
              </div>
            );
          }
        })}
        
        <div style={{ marginTop: '24px', borderTop: '1px solid #30363d', paddingTop: '16px' }}>
          <Button
            block
            icon={<LogoutOutlined />}
            onClick={() => {
              handleLogout();
              setDrawerVisible(false);
            }}
            style={{
              color: '#f85149',
              background: 'transparent',
              border: '1px solid #f85149',
              height: '48px'
            }}
          >
            退出登录
          </Button>
        </div>
      </div>
    );
  };

  return (
    <>
      <Header
        style={{
          background: '#161b22',
          padding: isMobile ? '0 12px' : '0 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 1px 4px rgba(0,0,0,.3)',
          borderBottom: '1px solid #30363d',
          height: '64px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '8px' : '16px' }}>
          {isMobile && (
            <Button
              type="text"
              icon={<MenuUnfoldOutlined />}
              onClick={() => setDrawerVisible(true)}
              style={{
                color: '#e6edf3',
                background: '#21262d',
                border: '1px solid #30363d',
                fontSize: '16px'
              }}
            />
          )}
          <div style={{ 
            fontSize: isMobile ? '16px' : '20px', 
            fontWeight: 'bold', 
            color: '#58a6ff',
            marginRight: isMobile ? '0' : '24px'
          }}>
            XiaBO
          </div>

          {!isMobile && (
            <Space size="middle">
              {renderMenu(menus)}
            </Space>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {!isMobile && (
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
              <Button 
                type="text" 
                icon={<Avatar icon={<UserOutlined />} />}
                style={{
                  color: '#e6edf3',
                  background: '#21262d',
                  border: '1px solid #30363d'
                }}
              >
                {me?.username || '用户'}
              </Button>
            </Dropdown>
          )}
          
          {isMobile && (
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight" arrow>
              <Avatar 
                icon={<UserOutlined />}
                style={{ 
                  backgroundColor: '#21262d',
                  border: '1px solid #30363d',
                  cursor: 'pointer'
                }}
              />
            </Dropdown>
          )}
        </div>
      </Header>

      <Drawer
        title="菜单"
        placement="left"
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        bodyStyle={{
          background: '#161b22',
          color: '#e6edf3'
        }}
        headerStyle={{
          background: '#161b22',
          color: '#e6edf3',
          borderBottom: '1px solid #30363d'
        }}
        style={{
          background: '#161b22'
        }}
      >
        {renderMobileMenu()}
      </Drawer>
    </>
  );
};

export default Navbar;
